// ============================================================
// INVOICES ADMIN HOOK - REACT QUERY OPTIMIZED
// ============================================================

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Invoice, InvoiceStatus } from '../types/invoices.types';

export interface InvoicesAdminState {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  stats: {
    total: number;
    paid: number;
    pending: number;
    cancelled: number;
    totalRevenue?: number;
    averageAmount?: number;
    pendingAmount?: number;
    currency?: string;
  };
}

export interface UseInvoicesAdminReturn extends InvoicesAdminState {
  statusFilter: string;
  searchQuery: string;
  updateFilter: (key: string, value: string) => void;
  updateSearch: (query: string) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  refresh: () => void;
}

const DEFAULT_PAGE_SIZE = 10;
const QUERY_KEY_BASE = ['invoices', 'admin'];

interface FetchInvoicesResult {
  items: any[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  stats: {
    total: number;
    paid: number;
    pending: number;
    cancelled: number;
    totalRevenue?: number;
    averageAmount?: number;
    pendingAmount?: number;
    currency?: string;
  };
}

async function fetchInvoices(params: {
  page: number;
  pageSize: number;
  status?: string;
  search?: string;
}): Promise<FetchInvoicesResult> {
  const queryParams = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.status && params.status !== 'all') {
    queryParams.set('status', params.status);
  }
  if (params.search?.trim()) {
    queryParams.set('search', params.search);
  }

  const { data } = await api.get(`/invoices/admin/paid-bookings?${queryParams.toString()}`);

  if (!data.success) {
    throw new Error(data.error || 'Erreur lors de la récupération des factures');
  }

  return data.data;
}

function transformInvoices(items: any[]): Invoice[] {
  return items.map((item: any) => ({
    id: item.id,
    bookingId: item.id,
    reference: item.reference ?? 'N/A',
    customerName: item.customerName ?? 'Client inconnu',
    customerEmail: item.customerEmail ?? '',
    customerPhone: item.customerPhone ?? '',
    companyName: item.companyName,
    companyPhone: item.specificDetails?.companyPhone || '',
    amount: item.totalAmount ?? 0,
    currency: item.currency || 'XAF',
    date: item.createdAt ?? new Date().toISOString(),
    paidAt: item.paidAt,
    status: (item.invoice?.status || 'PAID') as InvoiceStatus,
    pdfUrl: item.invoice?.pdfUrl,
    spaceType: item.spaceType ?? { id: '', name: 'N/A', slug: '' },
    invoice: item.invoice,
    installmentInfo: item.installmentInfo,
  }));
}

export function useInvoicesAdmin(): UseInvoicesAdminReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const statusFilter = searchParams.get('status') ?? 'all';
  const searchQuery = searchParams.get('search') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('pageSize') ?? DEFAULT_PAGE_SIZE);

  // Build query key for current filters
  const currentQueryKey = useMemo(
    () => [...QUERY_KEY_BASE, statusFilter, searchQuery, page, pageSize],
    [statusFilter, searchQuery, page, pageSize],
  );

  // React Query with caching and stale time
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: currentQueryKey,
    queryFn: () => fetchInvoices({ page, pageSize, status: statusFilter, search: searchQuery }),
    staleTime: 1000 * 60 * 5, // 5 minutes - data is considered fresh for 5 min
    gcTime: 1000 * 60 * 30, // 30 minutes - cache kept for 30 min
    placeholderData: (previousData) => {
      // Keep previous data while loading new data for better UX
      return previousData;
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Prefetch adjacent pages for instant navigation
  const prefetchAdjacentPages = useCallback(
    (currentPage: number, totalPages: number) => {
      const prevPage = currentPage - 1;
      const nextPage = currentPage + 1;

      if (prevPage >= 1) {
        queryClient.prefetchQuery({
          queryKey: [...QUERY_KEY_BASE, statusFilter, searchQuery, prevPage, pageSize],
          queryFn: () => fetchInvoices({ page: prevPage, pageSize, status: statusFilter, search: searchQuery }),
        });
      }

      if (nextPage <= totalPages) {
        queryClient.prefetchQuery({
          queryKey: [...QUERY_KEY_BASE, statusFilter, searchQuery, nextPage, pageSize],
          queryFn: () => fetchInvoices({ page: nextPage, pageSize, status: statusFilter, search: searchQuery }),
        });
      }
    },
    [queryClient, statusFilter, searchQuery, pageSize],
  );

  // Prefetch adjacent pages when data loads
  useMemo(() => {
    if (data && !isFetching) {
      prefetchAdjacentPages(page, data.totalPages);
    }
  }, [data, isFetching, page, prefetchAdjacentPages]);

  // Invalidate cache when filters change (for fresh stats)
  const invalidateInvoices = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_BASE });
  }, [queryClient]);

  const totalPages = data?.totalPages ?? 0;
  const total = data?.total ?? 0;

  const invoices = useMemo(() => {
    return data?.items ? transformInvoices(data.items) : [];
  }, [data?.items]);

  const stats = useMemo(
    () => ({
      total: total,
      paid: data?.stats?.paid ?? 0,
      pending: data?.stats?.pending ?? 0,
      cancelled: data?.stats?.cancelled ?? 0,
      totalRevenue: data?.stats?.totalRevenue,
      averageAmount: data?.stats?.averageAmount,
      pendingAmount: data?.stats?.pendingAmount,
      currency: data?.stats?.currency,
    }),
    [total, data?.stats],
  );

  // Actions
  const updateFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        if (value === 'all') {
          prev.delete(key);
        } else {
          prev.set(key, value);
        }
        prev.set('page', '1');
        return prev;
      });
      // Invalidate cache when filter changes to get fresh data
      invalidateInvoices();
    },
    [setSearchParams, invalidateInvoices],
  );

  const updateSearch = useCallback(
    (query: string) => {
      setSearchParams((prev) => {
        if (query.trim()) {
          prev.set('search', query);
        } else {
          prev.delete('search');
        }
        prev.set('page', '1');
        return prev;
      });
      invalidateInvoices();
    },
    [setSearchParams, invalidateInvoices],
  );

  const resetFilters = useCallback(() => {
    setSearchParams({});
    invalidateInvoices();
  }, [setSearchParams, invalidateInvoices]);

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        prev.set('page', String(newPage));
        return prev;
      });
    },
    [setSearchParams],
  );

  return {
    invoices,
    loading: isLoading,
    error: error ? error.message : null,
    pagination: {
      total,
      page,
      pageSize,
      totalPages,
    },
    stats,
    statusFilter,
    searchQuery,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
    refresh: invalidateInvoices,
  };
}
